import type { OAuthLicenseAssignment } from "~/server/utils/oauth/oauth.api.utils";
import type { Level } from "~/types/Level";

const isLicenseUnexpired = (license: OAuthLicenseAssignment) => {
  if (license.status !== "active") return false;
  if (!license.expiration_time) return true;

  return new Date(license.expiration_time).getTime() > Date.now();
};

export const hasCompanionUsableCurriculumLicense = ({
  level,
  licenses,
  assignedLicenseAssignmentIds,
  selectedProfileLicenseAssignmentId,
}: {
  level: Level;
  licenses: OAuthLicenseAssignment[];
  assignedLicenseAssignmentIds: Set<number>;
  selectedProfileLicenseAssignmentId?: number;
}) => {
  if (!level.external_product_sku) return false;

  return licenses.some((license) => {
    if (license.license_name !== level.external_product_sku) return false;
    if (assignedLicenseAssignmentIds.has(license.id)) return false;
    if (license.available_to_activate === true) return true;

    return (
      license.id === selectedProfileLicenseAssignmentId &&
      isLicenseUnexpired(license)
    );
  });
};

export const getCompanionSuggestedCurriculumLevels = ({
  levels,
  licenses,
  assignedLicenseAssignmentIds,
}: {
  levels: Level[];
  licenses: OAuthLicenseAssignment[];
  assignedLicenseAssignmentIds: Set<number>;
}) => {
  const unallocatedLicenseSkus = new Set(
    licenses
      .filter((license) => {
        return (
          license.available_to_activate === true &&
          !assignedLicenseAssignmentIds.has(license.id)
        );
      })
      .map((license) => license.license_name)
      .filter((licenseName) => licenseName.trim() !== "")
  );

  const suggestedSkuSet = new Set<string>();
  const suggestedLevelIds = new Set<string>();

  return [...levels]
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .filter((level) => {
      if (!level.id || !level.external_product_sku) return false;
      if (!unallocatedLicenseSkus.has(level.external_product_sku)) {
        return false;
      }
      if (suggestedLevelIds.has(level.id)) return false;
      if (suggestedSkuSet.has(level.external_product_sku)) return false;

      suggestedLevelIds.add(level.id);
      suggestedSkuSet.add(level.external_product_sku);

      return true;
    });
};
